/**
 * The small presentational pieces of the task workspace (TASK-UX1).
 *
 * Pulled out of ``AdminTaskDetailPage`` when that page grew a sidebar of
 * cards: the page is an orchestrator, and pills / rows / card chrome are
 * not orchestration (Regel 48). Behaviour is unchanged — these are the
 * same components, moved.
 *
 * S6 theme contract, preserved verbatim: light mode is carried by
 * semantic tokens (bg-card, text-foreground …) and dark mode is pinned
 * with ``dark:``-prefixed hex classes. A dark-only class as a STANDALONE
 * token is the S6 blocker and must never come back.
 */
import React from "react";

import { taskShape } from "../calendar/eventMapping";
import { formatDateOnly, formatDateTime } from "../../../utils/datetime";

// Status options aren't exported from TaskFormModal (only
// TASK_TYPE_OPTIONS / PRIORITY_OPTIONS are). Defined here rather than in
// the page so the pill and its label list live together — same five
// values as in AdminTasksPage.
export const STATUS_OPTIONS = [
  { value: "todo", label: "Offen" },
  { value: "in_progress", label: "In Arbeit" },
  { value: "blocked", label: "Blockiert" },
  { value: "done", label: "Erledigt" },
  { value: "cancelled", label: "Abgebrochen" },
];

// ──────────────────────────────────────────────────────────────────────
// Pills — S6 theme fix: LIGHT gets pastel token/palette tones, DARK
// keeps the previous hex appearance byte-for-byte via dark: pins.
// The light `border-*-200 bg-*-50` + rounded-full combinations are
// DELIBERATE: index.css's light-mode rules restyle exactly these
// combos into the app-wide solid status badges (see "Status / KPI:
// solid badges"), so these pills match every other admin page in
// light mode. The pastel text tones remain a correct fallback.
// ──────────────────────────────────────────────────────────────────────

const PILL_BASE =
  "inline-flex shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] " +
  "font-bold uppercase tracking-[0.05em]";

const PILL_NEUTRAL =
  "border-border bg-muted text-muted-foreground " +
  "dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#94a3b8]";

export function StatusPill({ value, labels }) {
  const label = labels?.[value] || value;
  const tone =
    {
      todo:
        "border-sky-200 bg-sky-50 text-sky-700 " +
        "dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#8fb3ff]",
      in_progress:
        "border-amber-200 bg-amber-50 text-amber-800 " +
        "dark:border-[#3a2f1f] dark:bg-[#1e160d] dark:text-[#f0b86e]",
      blocked:
        "border-red-200 bg-red-50 text-red-700 " +
        "dark:border-[#3a1f24] dark:bg-[#1e0d10] dark:text-[#ff6b6b]",
      done:
        "border-emerald-200 bg-emerald-50 text-emerald-700 " +
        "dark:border-[#1f3a2a] dark:bg-[#0d1e15] dark:text-[#6ee7a8]",
      cancelled:
        "border-border bg-muted text-muted-foreground " +
        "dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#4a5070]",
    }[value] || PILL_NEUTRAL;
  return <span className={`${PILL_BASE} ${tone}`}>{label}</span>;
}

export function PriorityPill({ value, labels }) {
  const label = labels?.[value] || value;
  const tone =
    {
      low: PILL_NEUTRAL,
      medium:
        "border-sky-200 bg-sky-50 text-sky-700 " +
        "dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#8fb3ff]",
      high:
        "border-amber-200 bg-amber-50 text-amber-800 " +
        "dark:border-[#3a2f1f] dark:bg-[#1e160d] dark:text-[#f0b86e]",
      urgent:
        "border-red-200 bg-red-50 text-red-700 " +
        "dark:border-[#3a1f24] dark:bg-[#1e0d10] dark:text-[#ff6b6b]",
    }[value] || PILL_NEUTRAL;
  return <span className={`${PILL_BASE} ${tone}`}>{label}</span>;
}

export function TaskTypePill({ value, labels }) {
  const label = labels?.[value] || value;
  return <span className={`${PILL_BASE} ${PILL_NEUTRAL}`}>{label}</span>;
}

// ──────────────────────────────────────────────────────────────────────
// Date display — uses the shared taskShape classifier so this view
// stays in lock-step with the calendar's understanding of due_at vs
// start_at/end_at.
// ──────────────────────────────────────────────────────────────────────

/** Returns the JSX block for the Termine row given a task. */
export function renderTermineBlock(task) {
  const shape = taskShape(task); // "A" | "B" | "C" | "D" | "E"
  const formatter = task.all_day ? formatDateOnly : formatDateTime;
  const allDayBadge = task.all_day ? (
    <span className="ml-2 text-[11px] font-medium text-muted-foreground dark:text-[#94a3b8]">
      (ganztägig)
    </span>
  ) : null;

  if (shape === "A" || shape === "B") {
    return (
      <p className="m-0 text-[13px] text-foreground dark:text-[#c5cbe0]">
        <span className="text-muted-foreground dark:text-[#4a5070]">
          Fällig:{" "}
        </span>
        <span className="font-medium tabular-nums">
          {formatter(task.due_at)}
        </span>
        {allDayBadge}
      </p>
    );
  }
  if (shape === "C" || shape === "D") {
    return (
      <p className="m-0 text-[13px] text-foreground dark:text-[#c5cbe0]">
        <span className="text-muted-foreground dark:text-[#4a5070]">
          Zeitraum:{" "}
        </span>
        <span className="font-medium tabular-nums">
          {formatter(task.start_at)} – {formatter(task.end_at)}
        </span>
        {allDayBadge}
      </p>
    );
  }
  // shape "E" — unscheduled
  return (
    <p className="m-0 text-[13px] text-muted-foreground dark:text-[#4a5070]">
      Keine Termine gesetzt.
    </p>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Card chrome
// ──────────────────────────────────────────────────────────────────────

export function Section({ title, children, action, headingId }) {
  return (
    <section className="rounded-[10px] border border-border bg-card p-4 dark:border-[#1e2130] dark:bg-[#13161f]">
      <header className="mb-3 flex items-center justify-between gap-3">
        <h2
          id={headingId}
          className="m-0 text-[12px] font-semibold uppercase tracking-[0.09em] text-muted-foreground dark:text-[#4a5070]"
        >
          {title}
        </h2>
        {action}
      </header>
      <div>{children}</div>
    </section>
  );
}

export function MetaRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border/60 py-2 last:border-b-0 dark:border-[#1e2130]/60">
      <span className="shrink-0 text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground dark:text-[#4a5070]">
        {label}
      </span>
      <span className="min-w-0 text-right text-[12px] text-foreground break-words dark:text-[#c5cbe0]">
        {value || "—"}
      </span>
    </div>
  );
}

export function EmptyPlaceholder({ children }) {
  return (
    <p className="m-0 text-[12px] text-muted-foreground dark:text-[#4a5070]">
      {children}
    </p>
  );
}

/**
 * "Selina Ieri" → "SI". Two letters from a first + last name, otherwise
 * the first two of whatever there is. Never a slice of a UUID: the
 * caller passes a resolved display name or nothing.
 */
export function personInitials(name) {
  const n = String(name || "").trim();
  if (!n) return "?";
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
  }
  return n.slice(0, 2).toUpperCase();
}
